import { Link } from 'react-router-dom';
import { toDisplayError } from '../../lib/errors';
import {
  DOSSIER_STATUT_LABELS,
  type DashboardSummary,
  type DossierStatut,
} from '../../lib/api-types';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/Table';
import { useDashboardSummary } from './hooks';

/** French label for a status code, falling back to the raw server code if unknown (future-proof). */
function statutLabel(code: string): string {
  return DOSSIER_STATUT_LABELS[code as DossierStatut] ?? code;
}

function countStatut(summary: DashboardSummary, statut: string): number {
  return summary.byStatut.find((s) => s.statut === statut)?.count ?? 0;
}

/** Dossiers carrying a PV component (PV seul or PV + Rapport) and a Rapport component respectively. */
function countScenarioContains(summary: DashboardSummary, token: 'PV' | 'RAPPORT'): number {
  return summary.byScenario
    .filter((s) => (token === 'PV' ? s.scenario.includes('PV') : s.scenario.includes('RAPPORT')))
    .reduce((acc, s) => acc + s.count, 0);
}

const STATUT_TONE: Record<string, 'neutral' | 'info' | 'success' | 'warning'> = {
  CREE: 'neutral',
  EN_COURS: 'info',
  DONNEES_COLLECTEES: 'info',
  CALCULE: 'warning',
  GENERE: 'warning',
  VERIFIE: 'warning',
  FINALISE: 'success',
  ARCHIVE: 'neutral',
};

function StatCard({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <Card>
      <CardContent className="py-4">
        <p className="text-sm font-medium text-slate-500">{label}</p>
        <p className="mt-1 text-3xl font-bold tabular-nums text-slate-900">{value}</p>
        {hint ? <p className="mt-1 text-xs text-slate-400">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

/**
 * Inline horizontal-bar chart for the dossier status distribution. No charting library (the repo
 * locks runtime deps): bars are plain SVG rects sized against the largest bucket. Decorative only —
 * every figure is also printed as text for accessibility.
 */
function StatusBars({ data }: { data: DashboardSummary['byStatut'] }): JSX.Element {
  if (data.length === 0) {
    return <p className="text-sm text-slate-500">Aucun dossier à représenter.</p>;
  }
  const max = Math.max(...data.map((d) => d.count), 1);
  const rowHeight = 28;
  const height = data.length * rowHeight;
  const labelWidth = 160;
  const barMax = 360;

  return (
    <svg
      role="img"
      aria-label="Répartition des dossiers par statut"
      viewBox={`0 0 ${labelWidth + barMax + 48} ${height}`}
      className="w-full"
      style={{ maxHeight: height }}
    >
      {data.map((d, i) => {
        const y = i * rowHeight;
        const width = Math.max((d.count / max) * barMax, d.count > 0 ? 4 : 0);
        return (
          <g key={d.statut}>
            <text x={0} y={y + 18} className="fill-slate-600" style={{ fontSize: 12 }}>
              {statutLabel(d.statut)}
            </text>
            <rect
              x={labelWidth}
              y={y + 6}
              width={width}
              height={16}
              rx={3}
              className="fill-brand-500"
            />
            <text
              x={labelWidth + width + 8}
              y={y + 18}
              className="fill-slate-700 tabular-nums"
              style={{ fontSize: 12 }}
            >
              {d.count}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * Tableau de bord opérationnel. Toutes les valeurs proviennent d'agrégations serveur LIVE
 * (`/dashboard/summary`) ; le périmètre (global ou limité à mes dossiers) est imposé par le serveur.
 */
export function DashboardPage(): JSX.Element {
  const query = useDashboardSummary();

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Tableau de bord</h1>
          <p className="text-sm text-slate-500">Vue opérationnelle de vos dossiers et documents.</p>
        </div>
        {query.data ? (
          <Badge tone={query.data.scope === 'GLOBAL' ? 'info' : 'neutral'}>
            {query.data.scope === 'GLOBAL' ? 'Périmètre global' : 'Mes dossiers'}
          </Badge>
        ) : null}
      </header>

      {query.isPending ? (
        <Spinner label="Chargement du tableau de bord…" />
      ) : query.isError ? (
        <Alert variant="error" title="Tableau de bord indisponible">
          {toDisplayError(query.error).message}
        </Alert>
      ) : (
        <DashboardContent summary={query.data} />
      )}
    </section>
  );
}

function DashboardContent({ summary }: { summary: DashboardSummary }): JSX.Element {
  const empty = summary.totalDossiers === 0;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard label="Dossiers" value={summary.totalDossiers} />
        <StatCard label="En cours" value={countStatut(summary, 'EN_COURS')} />
        <StatCard label="Finalisés" value={countStatut(summary, 'FINALISE')} />
        <StatCard label="Créés (30 j)" value={summary.createdLast30Days} />
        <StatCard
          label="Dossiers PV"
          value={countScenarioContains(summary, 'PV')}
          hint="Procès-verbaux"
        />
        <StatCard
          label="Dossiers Rapport"
          value={countScenarioContains(summary, 'RAPPORT')}
          hint="Rapports d'expertise"
        />
      </div>

      {/* Actions rapides */}
      <div className="flex flex-wrap gap-3">
        <Link
          to="/dossiers"
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          Nouveau dossier
        </Link>
        <Link
          to="/dossiers"
          className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Rechercher
        </Link>
        <Link
          to="/mandants"
          className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Clients
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Répartition par statut */}
        <Card>
          <CardHeader>
            <CardTitle>Répartition par statut</CardTitle>
          </CardHeader>
          <CardContent>
            <StatusBars data={summary.byStatut} />
          </CardContent>
        </Card>

        {/* Documents */}
        <Card>
          <CardHeader>
            <CardTitle>Documents ({summary.documents.total})</CardTitle>
          </CardHeader>
          <CardContent>
            {summary.documents.byStatus.length === 0 ? (
              <p className="text-sm text-slate-500">Aucun document généré.</p>
            ) : (
              <ul className="space-y-1.5">
                {summary.documents.byStatus.map((d) => (
                  <li key={d.status} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{d.status}</span>
                    <span className="font-medium tabular-nums text-slate-900">{d.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dossiers récents */}
      <Card>
        <CardHeader>
          <CardTitle>Dossiers récents</CardTitle>
        </CardHeader>
        <CardContent className="px-0 py-0">
          {empty || summary.recentDossiers.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-slate-500">
              Aucun dossier pour le moment.
            </p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Numéro</TH>
                  <TH>Client</TH>
                  <TH>Statut</TH>
                  <TH>Créé le</TH>
                </TR>
              </THead>
              <TBody>
                {summary.recentDossiers.map((d) => (
                  <TR key={d.id}>
                    <TD>
                      <Link
                        to={`/dossiers/${d.id}`}
                        className="font-medium text-brand-700 hover:underline"
                      >
                        {d.numero}
                      </Link>
                    </TD>
                    <TD>{d.mandantNom}</TD>
                    <TD>
                      <Badge tone={STATUT_TONE[d.statut] ?? 'neutral'}>
                        {statutLabel(d.statut)}
                      </Badge>
                    </TD>
                    <TD className="whitespace-nowrap text-slate-500">
                      {new Date(d.createdAt).toLocaleDateString('fr-FR')}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
